import { useServices } from '@/app/providers';
import type { CreateRestrictionRequest, Restriction } from '@yawi/types';
import { useCallback, useEffect, useState } from 'react';

export function useRestrictions() {
  const { guardRepo } = useServices();
  const [restrictions, setRestrictions] = useState<Restriction[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRestrictions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await guardRepo.getRestrictions();
      setRestrictions(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar restricciones');
    } finally {
      setLoading(false);
    }
  }, [guardRepo]);

  useEffect(() => {
    fetchRestrictions();
  }, [fetchRestrictions]);

  const createRestriction = useCallback(async (request: CreateRestrictionRequest) => {
    setActionLoading(true);
    setError(null);
    try {
      const restriction = await guardRepo.createRestriction(request);
      setRestrictions(prev => [...prev, restriction]);
      return restriction;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al crear restricción';
      setError(message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [guardRepo]);

  const deleteRestriction = useCallback(async (id: string) => {
    setActionLoading(true);
    setError(null);
    try {
      await guardRepo.deleteRestriction(id);
      setRestrictions(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al eliminar restricción';
      setError(message);
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, [guardRepo]);

  return {
    restrictions,
    loading,
    actionLoading,
    error,
    createRestriction,
    deleteRestriction,
    refresh: fetchRestrictions,
  };
}
